import React, { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useEmails } from "../../providers/EmailsContext";
import { useMeetings } from "../../providers/MeetingsContext";
import {
  FiCheckSquare,
  FiStar,
  FiPaperclip,
  FiClock,
  FiCalendar,
  FiVideo,
} from "react-icons/fi";

const TodoList = () => {
  const navigate = useNavigate();
  const { emails } = useEmails();
  const { meetings } = useMeetings();

  // Filter and sort todo items (emails + meetings)
  const todoItems = useMemo(() => {
    const allTodoItems = [];

    // Process emails
    if (emails) {
      const allEmails = [];

      // Get emails from needs_response category
      if (emails.needs_response && Array.isArray(emails.needs_response)) {
        emails.needs_response.forEach((item) => {
          if (item.email) {
            allEmails.push(item.email);
          }
        });
      }

      // Get emails from spam category
      if (emails.spam && Array.isArray(emails.spam)) {
        emails.spam.forEach((item) => {
          if (item.email) {
            allEmails.push(item.email);
          }
        });
      }

      // Get emails from notify category
      if (emails.notify && Array.isArray(emails.notify)) {
        emails.notify.forEach((item) => {
          if (item.email) {
            allEmails.push(item.email);
          }
        });
      }

      // Filter for todo emails and add type identifier
      const todoEmails = allEmails
        .filter((email) => email.is_todo === true)
        .map((email) => ({
          ...email,
          type: "email",
          sortDate: new Date(email.date),
        }));

      allTodoItems.push(...todoEmails);
    }

    // Process meetings
    if (meetings && Array.isArray(meetings)) {
      const todoMeetings = meetings
        .filter((meeting) => meeting.is_todo === true)
        .map((meeting) => ({
          ...meeting,
          type: "meeting",
          sortDate: new Date(
            meeting.start?.dateTime || meeting.start?.date || meeting.created
          ),
        }));

      allTodoItems.push(...todoMeetings);
    }

    // Sort by VIP status (for emails), then by date (newest first)
    return allTodoItems.sort((a, b) => {
      // First sort by VIP status (only applies to emails)
      if (a.type === "email" && b.type === "email") {
        if (a.is_vip !== b.is_vip) {
          return b.is_vip - a.is_vip; // true (1) comes before false (0)
        }
      } else if (a.type === "email" && a.is_vip) {
        return -1; // VIP emails come before meetings
      } else if (b.type === "email" && b.is_vip) {
        return 1; // VIP emails come before meetings
      }

      // Then sort by date (newest first)
      return b.sortDate - a.sortDate;
    });
  }, [emails, meetings]);

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffInHours = (now - date) / (1000 * 60 * 60);

      if (diffInHours < 24) {
        return date.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });
      } else {
        return date.toLocaleDateString([], {
          month: "short",
          day: "numeric",
        });
      }
    } catch (err) {
      console.error("Error formatting date:", err);
      return "";
    }
  };

  const formatMeetingTime = (meeting) => {
    if (!meeting.start) return "";

    const startTime = meeting.start.dateTime || meeting.start.date;
    const endTime = meeting.end?.dateTime || meeting.end?.date;

    if (!startTime) return "";

    try {
      const start = new Date(startTime);
      const startFormatted = start.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });

      if (endTime) {
        const end = new Date(endTime);
        const endFormatted = end.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });
        return `${startFormatted} - ${endFormatted}`;
      }

      return startFormatted;
    } catch (err) {
      console.error("Error formatting meeting time:", err);
      return "";
    }
  };

  const truncateText = (text, maxLength = 50) => {
    if (!text) return "";
    return text.length > maxLength
      ? text.substring(0, maxLength) + "..."
      : text;
  };

  const getMeetingLink = (meeting) => {
    // Check for hangoutLink first (Google Meet direct link)
    if (meeting.hangoutLink) {
      return meeting.hangoutLink;
    }

    // Check for conferenceData with entryPoints
    if (meeting.conferenceData?.entryPoints?.[0]?.uri) {
      return meeting.conferenceData.entryPoints[0].uri;
    }

    // Check for conferenceData with direct link property
    if (meeting.conferenceData?.link) {
      return meeting.conferenceData.link;
    }

    return null;
  };

  const handleItemClick = (item) => {
    if (item.type === "email") {
      // Navigate to emails or open email detail
      navigate("/emails");
    } else if (item.type === "meeting") {
      // Navigate to calendar or open meeting
      navigate("/calendar");
    }
  };

  const renderTodoItem = (item, index) => {
    if (item.type === "email") {
      return (
        <div
          key={`email-${item.thread_id || index}`}
          className="flex items-start hover:bg-gray-50 rounded p-3 sm:p-2 cursor-pointer transition-all duration-200"
          onClick={() => handleItemClick(item)}
        >
          <div className="flex-grow">
            <div className="flex items-center gap-2 mb-1">
              <FiPaperclip className="w-3 h-3 text-gray-500" />
              {item.is_vip && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                  <FiStar className="w-3 h-3 mr-1 fill-current" />
                  VIP
                </span>
              )}
            </div>

            <div className="flex justify-between items-start">
              <div className="flex-grow min-w-0">
                <p className="text-sm sm:text-xs font-medium text-gray-900 truncate">
                  {item.from_name || item.from_email}
                </p>
                <p className="text-sm sm:text-xs text-gray-700 mt-1">
                  {truncateText(item.subject)}
                </p>
                {item.deadline && (
                  <p className="text-xs text-red-600 mt-1 flex items-center">
                    <FiClock className="w-3 h-3 mr-1" />
                    Deadline: {formatDate(item.deadline)}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    } else if (item.type === "meeting") {
      const meetingLink = getMeetingLink(item);

      return (
        <div
          key={`meeting-${item.id || index}`}
          className="flex items-start hover:bg-gray-50 rounded p-3 sm:p-2 cursor-pointer transition-all duration-200"
          onClick={() => handleItemClick(item)}
        >
          <div className="flex-grow">
            <div className="flex items-center gap-2 mb-1">
              <FiCalendar className="w-3 h-3 text-blue-500" />
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                Meeting
              </span>
            </div>

            <div className="flex justify-between items-start">
              <div className="flex-grow min-w-0">
                <p className="text-sm sm:text-xs font-medium text-gray-900 truncate">
                  {item.summary || "Untitled Meeting"}
                </p>
                <p className="text-sm sm:text-xs text-gray-700 mt-1">
                  {formatMeetingTime(item)}
                </p>
                {item.location && (
                  <p className="text-xs text-gray-600 mt-1 truncate">
                    📍 {item.location}
                  </p>
                )}
                {item.attendees && item.attendees.length > 0 && (
                  <p className="text-xs text-gray-600 mt-1">
                    👥 {item.attendees.length} attendee
                    {item.attendees.length !== 1 ? "s" : ""}
                  </p>
                )}
              </div>

              {meetingLink && (
                <div className="ml-2 flex-shrink-0">
                  <a
                    href={meetingLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 transition-colors"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <FiVideo className="w-4 h-4" />
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }
  };

  return (
    <div className="bg-[#FCFCFC] w-[49%] p-4 flex flex-col rounded-lg shadow-sm shadow-gray-300 h-full">
      <div className="flex flex-col mb-3 sm:mb-2 justify-center flex-shrink-0">
        <h2 className="text-lg sm:text-base font-extrabold text-gray-600">
          Todo List
        </h2>
        <span className="text-sm sm:text-xs text-gray-500">
          You have {todoItems.length}{" "}
          {todoItems.length === 1 ? "activity" : "activities"} to do
        </span>
      </div>

      <div className="flex-grow flex flex-col max-h-[calc(100vh-60vh)] min-h-[calc(100vh-80vh)] overflow-y-auto">
        {todoItems.length === 0 ? (
          <div className="flex flex-col justify-center items-center flex-grow text-gray-500">
            <FiCheckSquare className="h-10 w-10 text-gray-400 mb-2" />
            <p className="text-center">No todo items found</p>
          </div>
        ) : (
          <div className="overflow-y-auto space-y-2 sm:space-y-1 custom-scrollbar">
            {todoItems.map((item, index) => renderTodoItem(item, index))}
          </div>
        )}
      </div>

      {todoItems.length > 0 && (
        <div className="mt-auto pt-3 flex-shrink-0">
          <button
            onClick={() => navigate("/calendar")}
            className="w-full bg-[#1F9BFF1A] text-[#1F9BFF] hover:bg-[#1F9BFF] hover:text-white font-medium py-2 sm:py-1.5 px-4 sm:px-3 rounded transition-all duration-200"
          >
            View Calendar
          </button>
        </div>
      )}

      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background-color: #d1d5db;
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background-color: #9ca3af;
        }
      `}</style>
    </div>
  );
};

export default TodoList;
